import { css } from '@emotion/react'
import styled from '@emotion/styled'

import Image from '../components/Image'
import ImageAspectRatio from '../components/ImageAspectRatio'
import Phone from '../icons/Phone.svg'
import Mail from '../icons/Mail.svg'
import LinkedIn from '../icons/LinkedIn.svg'
import Link from '../components/Link'
import CommaSeparatedList from '../components/CommaSeparatedList'

const Section = styled.section(
  ({ theme }) => css`
    @media screen and (min-width: ${theme.breakpoints.tablet}px) {
      padding: 0 ${theme.spacing.x2}px;
    }
  `,
)
const Container = styled.article(
  ({ theme }) => css`
    margin: 0 auto;
    max-width: ${theme.spacing.gridMaxWidth}px;
    padding: 0 0 ${theme.spacing.x4}px;

    @media screen and (min-width: ${theme.breakpoints.tablet}px) {
      padding: 0 0 ${theme.spacing.x10}px;
    }
  `,
)
const Grid = styled.div(
  ({ theme }) => css`
    @media screen and (min-width: ${theme.breakpoints.tablet}px) {
      display: flex;
      gap: ${theme.spacing.x10}px;
    }
  `,
)
const DesktopImageContainer = styled.div(
  ({ theme }) => css`
    display: none;

    @media screen and (min-width: ${theme.breakpoints.desktop}px) {
      display: block;
      flex: 0 0 auto;
      width: 456px;
    }
  `,
)
const MobileImageContainer = styled.div(
  ({ theme }) => css`
    margin-top: ${theme.spacing.x3}px;
    width: 100%;

    @media screen and (min-width: ${theme.breakpoints.tablet}px) {
      flex: 1 1 calc(50% - ${theme.spacing.x5 / 2}px);
      margin-top: 0;
      order: 1;
      width: auto;
    }

    @media screen and (min-width: ${theme.breakpoints.desktop}px) {
      display: none;
    }
  `,
)
const InfoContainer = styled.div(
  ({ theme }) => css`
    flex: 1 1 auto;
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    gap: 0 ${theme.spacing.x5}px;
    padding: 0 ${theme.spacing.x2}px;

    @media screen and (min-width: ${theme.breakpoints.tablet}px) {
      padding: 0;
    }

    @media screen and (min-width: ${theme.breakpoints.desktop}px) {
      flex-direction: column;
      padding: 0 ${theme.spacing.x2}px;
    }
  `,
)
const ContactInfo = styled.div(
  ({ theme }) => css`
    flex: 0 0 auto;

    @media screen and (min-width: ${theme.breakpoints.tablet}px) {
      flex: 1 1 calc(50% - ${theme.spacing.x5 / 2}px);
      order: 2;
    }
    @media screen and (min-width: ${theme.breakpoints.desktop}px) {
      display: flex;
      flex: 2 0 auto;
      flex-flow: column;
      justify-content: center;
      padding: ${theme.spacing.x4}px 0;
    }
  `,
)
const ContactTitle = styled.h2(
  ({ theme }) => css`
    ${theme.text.heading2(theme)}
    font-size: 30px;
    margin: 0 0 ${theme.spacing.x2 + theme.spacing.x0}px;

    @media screen and (min-width: ${theme.breakpoints.tablet}px) {
      margin-top: ${theme.spacing.x2 + theme.spacing.x0}px;
    }
    @media screen and (min-width: ${theme.breakpoints.desktop}px) {
      margin-top: 0;
    }
  `,
)
const ContactRow = styled.div(
  ({ theme }) => css`
    :not(:last-of-type) {
      margin-bottom: ${theme.spacing.x1}px;
    }
  `,
)
const ContactLink = styled(Link)(
  ({ theme }) => css`
    color: inherit;

    svg {
      height: 24px;
      margin-bottom: -2px;
      margin-right: ${theme.spacing.x1}px;
    }
  `,
)
const Details = styled.div(
  ({ theme }) => css`
    flex: 1 1 auto;
    padding: ${theme.spacing.x2}px ${theme.spacing.x2}px ${theme.spacing.x8}px;
    background: ${theme.colors.neutral1};
    display: grid;
    grid-auto-rows: max-content;
    gap: ${theme.spacing.x2}px;
    position: relative;
    z-index: ${theme.zIndex.relative1};

    @media screen and (min-width: ${theme.breakpoints.tablet}px) {
      grid-template-columns: repeat(2, 1fr);
      border-right: 5px solid ${theme.colors.primaryPurple};
      order: 3;
    }

    @media screen and (min-width: ${theme.breakpoints.desktop}px) {
      display: flex;
      flex-flow: column;
      padding: ${theme.spacing.x3}px;
      margin-top: ${theme.spacing.x5}px;
      margin-left: -${theme.spacing.x20}px;
      width: calc(100% + ${theme.spacing.x20}px);
    }
  `,
)
const DetailTitle = styled.h2(
  ({ theme }) => css`
    font-weight: bold;
    margin: 0;
    color: ${theme.colors.primaryPurpleDark};
    font-size: 16px;
  `,
)

interface Props {
  name: string
  photo: ImageInfo
  email?: string
  phone?: string
  linkedIn?: string
  disciplines?: string[]
  specialisms?: string[]
  qualifications?: string
  experience?: string
  getInTouchLabel: string
  linkedInProfileLabel: string
  disciplinesLabel: string
  specialismsLabel: string
  qualificationsLabel: string
  experienceLabel: string
}

const ExpertProfileSection = ({
  name,
  photo,
  email,
  phone,
  linkedIn,
  disciplines,
  specialisms,
  qualifications,
  experience,
  getInTouchLabel,
  linkedInProfileLabel,
  disciplinesLabel,
  specialismsLabel,
  qualificationsLabel,
  experienceLabel,
}: Props) => (
  <Section>
    <Container>
      <Grid>
        <DesktopImageContainer>
          <ImageAspectRatio aspectRatio={581 / 456}>
            <Image
              src={photo}
              alt={photo.alt || name}
              sizes="(min-width: 768px) 456px, 100vw"
              fill
              aspectRatio="456:581"
              priority
            />
          </ImageAspectRatio>
        </DesktopImageContainer>

        <InfoContainer>
          <ContactInfo>
            <ContactTitle>{getInTouchLabel}</ContactTitle>
            {email && (
              <ContactRow>
                <ContactLink href={`mailto:${email}`}>
                  <Mail aria-label="Email" />
                  {email}
                </ContactLink>
              </ContactRow>
            )}
            {phone && (
              <ContactRow>
                <ContactLink href={`tel:${phone}`}>
                  <Phone aria-label="Phone" />
                  {phone}
                </ContactLink>
              </ContactRow>
            )}
            {linkedIn && (
              <ContactRow>
                <ContactLink href={linkedIn}>
                  <LinkedIn aria-label="LinkedIn" />
                  {linkedInProfileLabel}
                </ContactLink>
              </ContactRow>
            )}
          </ContactInfo>

          <MobileImageContainer>
            <ImageAspectRatio aspectRatio={332 / 343}>
              <Image
                src={photo}
                alt={photo.alt || name}
                sizes="(min-width: 768px) 456px, 100vw"
                fill
                aspectRatio="343:332"
              />
            </ImageAspectRatio>
          </MobileImageContainer>

          {(disciplines || specialisms || qualifications || experience) && (
            <Details>
              {disciplines && (
                <div>
                  <DetailTitle>{disciplinesLabel}</DetailTitle>
                  <CommaSeparatedList>{disciplines}</CommaSeparatedList>
                </div>
              )}
              {specialisms && (
                <div>
                  <DetailTitle>{specialismsLabel}</DetailTitle>
                  <CommaSeparatedList>{specialisms}</CommaSeparatedList>
                </div>
              )}
              {qualifications && (
                <div>
                  <DetailTitle>{qualificationsLabel}</DetailTitle>
                  {qualifications}
                </div>
              )}
              {experience && (
                <div>
                  <DetailTitle>{experienceLabel}</DetailTitle>
                  {experience}
                </div>
              )}
            </Details>
          )}
        </InfoContainer>
      </Grid>
    </Container>
  </Section>
)

export default ExpertProfileSection
